import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE
} from '@angular/material/core';
import {
  OrderDirectory,
  Plan,
  PlanningDetails
} from '@authority-labs/api-interfaces';
import { Path } from '@authority-labs/enums';
import { v4 as uuidv4 } from 'uuid';
import { UploadDirectory } from '@check-list/app/classes/models/upload-directory.model';
import { UploadFile } from '@check-list/app/classes/models/upload-file.model';
import { NotesBankDialogService } from '@check-list/app/shared/dialogs/notes-bank-dialog/notes-bank-dialog.service';
import { DirectoryDialogService } from '@check-list/shared/dialogs';
import { CommonService, FilesService } from '@check-list/shared/services';
import { DraftStateService } from '../../inspection-draft/services/draft-state.service';

const DATE_FORMAT = {
  parse: {
    dateInput: 'DD.MM.YYYY'
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMM YYYY'
  }
};

@Component({
  selector: 'authority-labs-plans',
  templateUrl: './plans.component.html',
  styleUrls: ['./plans.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'he-IL' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMAT }
  ]
})
export class PlansComponent implements OnInit, OnChanges {
  @Input() orderId: number;
  @Input() orderDirectory: OrderDirectory;
  @Input() planningDetails: PlanningDetails;
  @Input() readonly: boolean;
  @Input() printPreview: boolean;
  @Input() type: string;
  @Input() inkSectionKey: string;
  @Output() dataChange: EventEmitter<void> = new EventEmitter();

  displayedColumns = ['name', 'identificationCode', 'edition', 'date', 'notes'];

  selectedPlan: Plan;
  uploading: UploadFile;
  uploadDirectory: UploadDirectory;
  uploadQueueChanged = new EventEmitter<UploadFile>();

  get today() {
    return new Date().toLocaleDateString('he');
  }

  get inkMode(): boolean {
    return !!this.inkState;
  }

  constructor(
    private commonService: CommonService,
    private directoryDialogService: DirectoryDialogService,
    private readonly notesBankDialogService: NotesBankDialogService,
    public filesService: FilesService,
    @Optional() @Inject(DraftStateService) private inkState: DraftStateService
  ) {
    this.uploadQueueChanged.subscribe((file) => this.onAddingFile(file));
  }

  ngOnInit() {
    this.buildColumns();
    this.ensurePlanGuids();

    if (!(this.readonly || this.printPreview)) {
      if (this.orderId && this.orderDirectory) {
        this.uploadDirectory = new UploadDirectory({
          namePath: 'plans',
          uploadEntity: { id: this.orderId, type: Path.Orders },
          id: this.orderDirectory.plans,
          filesListChanged: this.uploadQueueChanged
        });
      }
    }
    if (this.printPreview && this.planningDetails.plans.length === 0) {
      this.planningDetails.plans.push({});
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.readonly && !changes.readonly.firstChange) {
      this.buildColumns();
    }
  }

  protected buildColumns(): void {
    const cols = ['name', 'identificationCode', 'edition', 'date', 'notes'];
    if (!(this.readonly || this.printPreview)) {
      cols.unshift('drag');
      cols.push('file', 'actions');
    } else if (this.readonly && !this.printPreview) {
      cols.push('file');
    }
    this.displayedColumns = cols;
  }

  drop(event: CdkDragDrop<Plan[]>) {
    const prevIndex = this.planningDetails.plans.findIndex(
      (d) => d === event.item.data
    );
    moveItemInArray(this.planningDetails.plans, prevIndex, event.currentIndex);
    this.planningDetails.plans = [...this.planningDetails.plans];

    this.onChange();
  }

  addPlan(plan: Plan = new Plan()): void {
    this.planningDetails.plans = [plan, ...this.planningDetails.plans];
  }

  remove(plan: Plan): void {
    if (this.inkState && this.inkSectionKey && plan.guid) {
      this.inkState.removeFieldsByPrefix(
        `${this.inkSectionKey}:plans:${plan.guid}:`
      );
    }
    const index = this.planningDetails.plans.indexOf(plan);
    this.planningDetails.plans.splice(index, 1);
    this.planningDetails.plans = [...this.planningDetails.plans];
    this.onChange();
  }

  viewFile(fileId: string): void {
    this.commonService.viewFile(fileId);
  }

  addPlansByDocs(): void {
    this.directoryDialogService
      .selectFiles(
        this.orderId,
        this.planningDetails.plans.map((item) => item.fileId)
      )
      .afterClosed()
      .subscribe((files) => {
        if (files) {
          files.forEach((file) => {
            const plan = new Plan({
              name: file.name.replace(`.${file.fileExtension}`, ''),
              fileId: file.id,
              fileName: file.name
            });

            this.addPlan(plan);
          });
          this.onChange();
        }
      });
  }

  attachDoc(plan: Plan): void {
    this.directoryDialogService
      .selectFile(
        this.orderId,
        this.planningDetails.plans.map((item) => item.fileId)
      )
      .afterClosed()
      .subscribe((file) => {
        if (file) {
          plan.fileId = file.id;
          plan.fileName = file.name;
          this.onChange();
        }
      });
  }

  onChange(): void {
    this.dataChange.emit();
  }

  cancelUpload() {
    this.uploading?.cancelUpload();
  }

  selectFile(plan: Plan): void {
    this.selectedPlan = plan;
  }

  showNotesBank(e: Event, element: Plan) {
    e.stopPropagation();
    this.notesBankDialogService
      .show(this.type)
      .afterClosed()
      .subscribe((selected) => {
        if (selected) {
          element.name = selected;
          this.onChange();
        }
      });
  }

  planFieldId(plan: Plan, fieldKey: string): string {
    if (!this.inkSectionKey || !plan.guid) return '';
    return `${this.inkSectionKey}:plans:${plan.guid}:${fieldKey}`;
  }

  private ensurePlanGuids(): void {
    if (!this.planningDetails?.plans) return;
    let changed = false;
    for (const plan of this.planningDetails.plans) {
      if (!plan.guid) {
        plan.guid = uuidv4();
        changed = true;
      }
    }
    if (changed) {
      this.onChange();
    }
  }

  private onAddingFile(file: UploadFile) {
    this.uploading = file;
    this.filesService.upload([file]);
    this.uploading.uploadDestroyed$.subscribe(() => {
      if (file?.file?.response?.id) {
        this.selectedPlan.fileId = file.file.response.id;
        this.selectedPlan.fileName = file.file.name;
        this.selectedPlan.name = file.file.name;
        this.onChange();
      }
    });
  }
}
